import { Platform } from 'react-native'
import * as Application from 'expo-application'
import Constants from 'expo-constants'
import { env } from '../env'
import { IOS_APP_STORE_URL } from '../constants/app-store'
import { SecureStorageAdapter } from '../storage/secure-storage'
import { fetchWithTimeout } from '../utils/fetch-with-timeout'

export interface PlatformVersionPolicy {
  minBuildVersion: number
  latestBuildVersion: number
  latestVersion: string
  storeUrl: string
}

export interface AppVersionPolicy {
  enforceOtaUpdate: boolean
  enforceStoreUpdate: boolean
  forceLatestStoreBuild: boolean
  android: PlatformVersionPolicy
  ios: PlatformVersionPolicy
}

const DEFAULT_ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=app.tago.rent'
const DEFAULT_IOS_STORE_URL = env.EXPO_PUBLIC_IOS_APP_STORE_URL ?? IOS_APP_STORE_URL
const POLICY_CACHE_KEY = 'tago_app_version_policy_v1'
const POLICY_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000

export const appUpdatePolicyService = {
  async fetchPolicy(signal?: AbortSignal): Promise<AppVersionPolicy> {
    // The mandatory-update gate blocks the whole app until this resolves. It
    // has its own 8s race in use-mandatory-update-gate.ts, but a fetch that
    // never settles also leaks a live request; bound it at the source too.
    const response = await fetchWithTimeout(
      `${env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/app-version-policy`,
      {
        method: 'GET',
        headers: {
          apikey: env.EXPO_PUBLIC_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        ...(signal ? { signal } : {}),
      },
      { timeoutMs: 8_000 },
    )

    if (!response.ok) {
      throw new Error(`App version policy failed with ${response.status}`)
    }

    const policy = normalizePolicy(await response.json())
    await this.cachePolicy(policy)
    return policy
  },

  async getCachedPolicy(): Promise<AppVersionPolicy | null> {
    try {
      const raw = await SecureStorageAdapter.getItem(POLICY_CACHE_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw) as { cachedAt?: unknown; policy?: unknown }
      const cachedAt = typeof parsed.cachedAt === 'number' ? parsed.cachedAt : 0
      if (Date.now() - cachedAt > POLICY_CACHE_MAX_AGE_MS) return null
      return normalizePolicy(parsed.policy)
    } catch {
      return null
    }
  },

  currentBuildVersion(): number {
    if (Platform.OS === 'android') {
      return numberFromUnknown(
        Application.nativeBuildVersion ?? Constants.platform?.android?.versionCode,
      )
    }

    if (Platform.OS === 'ios') {
      return numberFromUnknown(
        Application.nativeBuildVersion ?? Constants.platform?.ios?.buildNumber,
      )
    }

    return Number.MAX_SAFE_INTEGER
  },

  async cachePolicy(policy: AppVersionPolicy): Promise<void> {
    try {
      await SecureStorageAdapter.setItem(
        POLICY_CACHE_KEY,
        JSON.stringify({ cachedAt: Date.now(), policy }),
      )
    } catch {
      // Non-sensitive cache only. A failed write must never affect app launch.
    }
  },
}

function normalizePolicy(value: unknown): AppVersionPolicy {
  const raw = isRecord(value) ? value : {}
  return {
    enforceOtaUpdate: booleanFromUnknown(raw.enforceOtaUpdate, true),
    enforceStoreUpdate: booleanFromUnknown(raw.enforceStoreUpdate, false),
    forceLatestStoreBuild: booleanFromUnknown(raw.forceLatestStoreBuild, false),
    android: normalizePlatformPolicy(raw.android, DEFAULT_ANDROID_STORE_URL),
    ios: normalizePlatformPolicy(raw.ios, DEFAULT_IOS_STORE_URL),
  }
}

function normalizePlatformPolicy(value: unknown, fallbackStoreUrl: string): PlatformVersionPolicy {
  const raw = isRecord(value) ? value : {}
  return {
    minBuildVersion: numberFromUnknown(raw.minBuildVersion),
    latestBuildVersion: numberFromUnknown(raw.latestBuildVersion),
    latestVersion: typeof raw.latestVersion === 'string' ? raw.latestVersion : '',
    storeUrl: typeof raw.storeUrl === 'string' && raw.storeUrl ? raw.storeUrl : fallbackStoreUrl,
  }
}

function booleanFromUnknown(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function numberFromUnknown(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed)) return parsed
  }
  return 0
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
